import React, { useState, useEffect } from 'react';
import { projectsAPI } from '../../utils/api';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Loading from '../../components/common/Loading';
import ErrorMessage from '../../components/common/ErrorMessage';
import Modal from '../../components/common/Modal';
import ProjectForm from '../../components/admin/ProjectForm';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { formatCategory, formatDateShort } from '../../utils/formatters';

const ProjectManager = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentProject, setCurrentProject] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await projectsAPI.getAll();
      setProjects(response.data.data || []);
    } catch (err) {
      setError(err.message || 'Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this project?')) {
      return;
    }

    try {
      await projectsAPI.delete(id);
      setProjects(projects.filter((p) => p._id !== id));
    } catch (err) {
      alert(err.message || 'Failed to delete project');
    }
  };

  const handleOpenModal = (project = null) => {
    setCurrentProject(project);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentProject(null);
  };

  const handleSubmit = async (formData) => {
    try {
      setSubmitting(true);
      if (currentProject) {
        // Update existing project
        const response = await projectsAPI.update(currentProject._id, formData);
        setProjects(
          projects.map((p) =>
            p._id === currentProject._id ? response.data.data : p
          )
        );
      } else {
        // Create new project
        const response = await projectsAPI.create(formData);
        setProjects([response.data.data, ...projects]);
      }
      handleCloseModal();
    } catch (err) {
      alert(err.message || 'Failed to save project');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Loading text="Loading projects..." />;
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-heading font-bold text-secondary mb-2">
            Manage Projects
          </h1>
          <p className="text-text-primary font-body">
            Add, edit, or delete portfolio projects
          </p>
        </div>
        <Button variant="primary" onClick={() => handleOpenModal()}>
          <FaPlus className="inline mr-2" />
          New Project
        </Button>
      </div>

      {/* Error Message */}
      {error && <ErrorMessage message={error} className="mb-6" />}

      {/* Projects List */}
      {projects.length === 0 ? (
        <Card className="p-8 text-center">
          <p className="text-text-primary font-body">
            No projects yet. Create your first project!
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {projects.map((project) => (
            <Card key={project._id} className="p-6">
              <div className="flex items-start space-x-4">
                {/* Cover Image */}
                <img
                  src={project.coverImage}
                  alt={project.title}
                  className="w-24 h-24 object-cover rounded"
                />

                {/* Project Info */}
                <div className="flex-1">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-xl font-heading font-semibold text-secondary">
                        {project.title}
                      </h3>
                      <div className="flex items-center gap-3 mt-1 text-sm text-gray-600 font-body">
                        <span className="text-accent font-medium">
                          {formatCategory(project.category)}
                        </span>
                        {project.date && <span>• {formatDateShort(project.date)}</span>}
                        {project.featured && (
                          <span className="bg-accent text-white px-2 py-1 rounded text-xs">
                            Featured
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleOpenModal(project)}
                      >
                        <FaEdit />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(project._id)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <FaTrash />
                      </Button>
                    </div>
                  </div>

                  <p className="mt-2 text-text-primary font-body line-clamp-2">
                    {project.description}
                  </p>

                  {project.clientName && (
                    <p className="mt-2 text-sm text-gray-600 font-body">
                      Client: {project.clientName}
                    </p>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modal for Create/Edit */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={currentProject ? 'Edit Project' : 'Create New Project'}
        size="xlarge"
      >
        <ProjectForm
          project={currentProject}
          onSubmit={handleSubmit}
          onCancel={handleCloseModal}
          loading={submitting}
        />
      </Modal>
    </div>
  );
};

export default ProjectManager;
